import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import "../assets/css/FAQ.css";

const VerifiedBadge = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" style={{ marginLeft: "4px", verticalAlign: "text-bottom", display: "inline-block" }}>
        <path fill="#1877F2" d="M12,2C6.477,2,2,6.477,2,12c0,5.523,4.477,10,10,10s10-4.477,10-10C22,6.477,17.523,2,12,2z"></path>
        <path fill="#ffffff" d="M10.47,16.59L6.15,12.26l1.41-1.41l2.91,2.91l6.97-6.97l1.41,1.41L10.47,16.59z"></path>
    </svg>
);

const FAQ = ({ isStandalonePage = true }) => {
    const [activeIndex, setActiveIndex] = useState(null);
    const appFrontendUrl = process.env.REACT_APP_URL || "https://app.comparedly.com";

    useEffect(() => {
        if (isStandalonePage) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [isStandalonePage]);

    const faqData = [
        {
            question: "What platforms does Comparedly support?",
            answer: "Comparedly currently provides deep competitive intelligence for Facebook, Instagram, and YouTube. We are constantly expanding our platform support to include TikTok and X (Twitter) in future updates."
        },
        {
            question: "Is Comparedly an officially verified platform?",
            answer: (
                <>
                    Yes! Comparedly relies on official API access. We are a <strong>Meta Verified Tech Provider</strong> <VerifiedBadge />, ensuring our platform complies with all data privacy guidelines for Facebook and Instagram. We are also an officially approved <strong>YouTube API Services Developer</strong> <VerifiedBadge />, guaranteeing the highest standard of data accuracy and secure reporting for your agency.
                </>
            )
        },
        {
            question: "How does the '1-Click Live Demo' work?",
            answer: "Our 1-Click Live Demo provides a selection of sample profiles with live data. It's built primarily to let you get a feel for Comparedly’s speed and UI instantly, without needing to sign up or create an account."
        },
        {
            question: "Is there a free trial?",
            answer: "Yes! We offer a 14-day free trial on all our plans. No credit card is required to start, giving you full access to our benchmarking and reporting tools before you commit."
        },
        {
            question: "Can I automate client reporting?",
            answer: "Absolutely. Comparedly allows you to schedule automated PDF snapshots that can be sent to your team or directly to your clients on a weekly or monthly basis."
        },
        {
            question: "What is Comparedly's core benefit?",
            answer: "Our core USP is Pro-Grade Competitor Analysis. While other tools focus on your own posts, Comparedly is built to benchmark your performance directly against your rivals, giving you the context needed to win your market."
        },
        {
            question: "How fast can I get competitor insights?",
            answer: "In seconds. We’ve optimised our engine to pull real-time benchmarking data across Facebook, Instagram, and YouTube almost instantly, so you can spend your time acting on data rather than collecting it."
        },
        {
            question: "Why use Comparedly instead of manual spreadsheets?",
            answer: "Spreadsheets are static and human-error prone. Comparedly automates the daily grind of data collection, providing live updates, visual growth curves, and automated snapshots that would take an analyst hours to build manually."
        },
        {
            question: "How is Comparedly different from Sprout Social?",
            answer: "While Sprout Social is an 'All-in-One' suite focusing heavily on publishing and engagement, Comparedly is built specifically for 'Intelligence and Reporting.' We offer deeper competitive benchmarking at a fraction of the enterprise cost, with no restrictive per-seat pricing."
        },
        {
            question: "Do you offer white-label reporting?",
            answer: "Yes, our Pro and Ultra plans include the ability to customize reports with your agency's branding, allowing you to present data with professional clarity."
        }
    ];

    const toggleFAQ = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    return (
        <div className={isStandalonePage ? "faq-page" : "faq-section"}>
            {isStandalonePage && (
                <Helmet>
                    <title>Frequently Asked Questions | Comparedly</title>
                    <meta name="description" content="Find answers to common questions about Comparedly's features, pricing, and how we help agencies automate competitor analysis." />
                    <meta property="og:title" content="FAQ | Comparedly Support" />
                    <meta property="og:description" content="Got questions? We've got answers. Explore our FAQ to learn how Comparedly simplifies social media benchmarking." />
                </Helmet>
            )}
            {isStandalonePage && <Navbar />}

            <main className="faq-container" id="faq">
                <header className="faq-header">
                    <h1 className="faq-title">Frequently Asked Questions</h1>
                    <p className="faq-subtitle">Everything you need to know about Comparedly.</p>
                </header>

                <section className="faq-list">
                    {faqData.map((item, index) => (
                        <div
                            key={index}
                            className={`faq-item ${activeIndex === index ? 'active' : ''}`}
                            onClick={() => toggleFAQ(index)}
                        >
                            <div className="faq-question">
                                <h3>{item.question}</h3>
                                <span className="faq-icon">{activeIndex === index ? '−' : '+'}</span>
                            </div>
                            <div className="faq-answer">
                                <p>{item.answer}</p>
                            </div>
                        </div>
                    ))}
                </section>

                <section className="faq-cta">
                    <h2>Still have questions?</h2>
                    <p>We're here to help you reclaim your agency's ROI.</p>
                    <button
                        className="btn-primary"
                        onClick={() => window.location.href = `${appFrontendUrl}/auth/register`}
                    >
                        Start Your Free Trial
                    </button>
                </section>
            </main>

            {isStandalonePage && <Footer />}
        </div>
    );
};

export default FAQ;
